import useAuthStore from './store/authStore';
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { StoreSettingsProvider } from './context/StoreSettingsContext';
import { SystemProvider } from './context/SystemContext';
import axiosInstance from './api/axiosInstance';
// Pages
import HomePage from './pages/HomePage';
import ShopPage from './pages/ShopPage';
import ProductPage from './pages/ProductPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import OrderSuccessPage from './pages/OrderSuccessPage';
import NotFoundPage from './pages/NotFoundPage';
import MaintenancePage from './pages/MaintenancePage';
import LogoutSyncPage from './pages/LogoutSyncPage';
import TermsPage from './pages/TermsPage';
import ContactPage from './pages/ContactPage';
import FAQPage from './pages/FAQPage';
import ShippingPolicyPage from './pages/ShippingPolicyPage';
import ReturnPolicyPage from './pages/ReturnPolicyPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import FeaturesPage from './pages/FeaturesPage';
import RecipesPage from './pages/RecipesPage';
import RecipeDetailPage from './pages/RecipeDetailPage';
// Components
import ProtectedRoute from './components/ProtectedRoute';
import AnnouncementBanner from './components/AnnouncementBanner';
import CookieBanner from './components/CookieBanner';
import SystemStatusBanner from './components/SystemStatusBanner';
import FeatureGate from './components/FeatureGate';
import NewsletterPopup from './components/NewsletterPopup';
import { initGlobalErrorLogging } from './utils/errorLogger';
import SitemapPage from './pages/SitemapPage';
import ScrollToTop from './components/ScrollToTop';
// Inner app — has access to Router context for useLocation
function AppRoutes() {
  return (
    <SystemProvider>
      <ScrollToTop />
      <SystemStatusBanner />
      <FeatureGate>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/shop" element={<ShopPage />} />
          <Route path="/product/:id" element={<ProductPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/logout-sync" element={<LogoutSyncPage />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/shipping-policy" element={<ShippingPolicyPage />} />
          <Route path="/return-policy" element={<ReturnPolicyPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/recipes" element={<RecipesPage />} />
          <Route path="/recipes/:id" element={<RecipeDetailPage />} />
          <Route path="/cart" element={<ProtectedRoute><CartPage /></ProtectedRoute>} />
          <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
          <Route path="/order-success" element={<ProtectedRoute><OrderSuccessPage /></ProtectedRoute>} />
          <Route path="/sitemap" element={<SitemapPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </FeatureGate>
      <AnnouncementBanner />
      <CookieBanner />
      <NewsletterPopup />
    </SystemProvider>
  );
}

function App() {
  const [maintenance, setMaintenance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authToken = params.get('auth_token');
    const authUser = params.get('auth_user');
    if (authToken && authUser) {
      const decodedUser = decodeURIComponent(authUser);
      localStorage.setItem('aurora_token', authToken);
      localStorage.setItem('aurora_user', decodedUser);
      try {
        useAuthStore.getState().setAuth(authToken, JSON.parse(decodedUser));
      } catch { useAuthStore.getState().refreshFromStorage(); }
      window.dispatchEvent(new Event('storage'));
      window.history.replaceState({}, '', window.location.pathname);
    } else {
      if (!localStorage.getItem('aurora_token')) localStorage.removeItem('aurora_user');
      useAuthStore.getState().refreshFromStorage();
    }
  }, []);

  useEffect(() => { checkMaintenance(); }, []);
  useEffect(() => { initGlobalErrorLogging(); }, []);

  const checkMaintenance = async () => {
    try {
      const res = await axiosInstance.get('/system/status');
      if (res.data.success) setMaintenance(res.data.data);
    } catch {
      try {
        const legacy = await axiosInstance.get('/maintenance');
        if (legacy.data.success) setMaintenance(legacy.data.data);
      } catch (e) {}
    } finally {
      setLoading(false);
    }
  };


  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh' }}>
      <div style={{ width:32, height:32, border:'3px solid #E2E8F0', borderTopColor:'#29ABE2', borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
    </div>
  );

  if (maintenance?.maintenanceMode) {
    return (
      <MaintenancePage
        message={maintenance.maintenanceMessage || maintenance.message}
        estimatedReturn={maintenance.estimatedReturn}
        contactEmail={maintenance.contactEmail}
      />
    );
  }

  return (
    <StoreSettingsProvider>
      <Router>
          <Toaster position="top-right" toastOptions={{ duration: 3000, style: { fontFamily: 'Inter, sans-serif', fontSize: '14px' } }} />
          <AppRoutes />
      </Router>
    </StoreSettingsProvider>
  );
}

export default App;